import { Injectable } from '@angular/core';

/**
 * Campaign values read from a request-offer deep link (CLP-1259).
 *
 * `utm_parameter` is the only value sent to the backend (CLP-1258 →
 * Salesforce). The `utm_*` values are for GA4 only.
 */
export interface CampaignValues {
  utm_parameter?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
}

export const CAMPAIGN_KEYS: ReadonlyArray<keyof CampaignValues> = [
  'utm_parameter',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
];

/** Query parameter carrying the product to preselect in "Offerte anfragen". */
export const DEEP_LINK_PRODUCT_PARAM = 'product';

const CAMPAIGN_STORAGE_KEY = 'smzh.campaignAttribution';
const PENDING_PRODUCT_STORAGE_KEY = 'smzh.pendingOfferProduct';

/** Guards against oversized or malformed values coming in from a URL. */
const MAX_VALUE_LENGTH = 255;

/**
 * Keeps campaign attribution from a deep link for the browser session.
 *
 * Storage is `sessionStorage`, which matches the ticket's session lifetime:
 * - survives the redirect to /auth, reloads, login and registration in the tab;
 * - is cleared when the tab or browser closes ("session ends");
 * - is cleared on logout by `AuthStateService.logOut()` (`sessionStorage.clear()`).
 *
 * Values are NOT cleared after a submit: they apply to every request-offer in
 * the session, also for other products than the one in the link.
 *
 * Every storage access is wrapped, so a blocked or unavailable storage
 * degrades to "no attribution" instead of breaking the app.
 */
@Injectable({ providedIn: 'root' })
export class CampaignAttributionService {
  /**
   * Reads the campaign values and the `product` from a URL and stores them.
   * Runs once at app start, before the router's first navigation, so the
   * values are captured before `authGuard` redirects to /login and drops the
   * query string.
   *
   * Last click wins: when the URL carries at least one campaign value, the
   * stored set is replaced as a whole (not merged), so values from an older
   * link cannot linger next to the new ones. A URL without campaign values
   * leaves the stored set untouched.
   */
  captureFromUrl(url: string = this.currentUrl()): void {
    const params = this.parseParams(url);
    if (!params) return;

    const campaign: CampaignValues = {};
    for (const key of CAMPAIGN_KEYS) {
      const value = this.sanitize(params.get(key));
      if (value) campaign[key] = value;
    }
    if (Object.keys(campaign).length > 0) {
      this.write(CAMPAIGN_STORAGE_KEY, JSON.stringify(campaign));
    }

    // A link that carries `product` asks for "Offerte anfragen", even when the
    // value is empty (`product=`): then the flow opens on step 1 without a
    // preselection, which is stored as "". Without the parameter nothing opens.
    if (params.has(DEEP_LINK_PRODUCT_PARAM)) {
      this.write(
        PENDING_PRODUCT_STORAGE_KEY,
        this.sanitize(params.get(DEEP_LINK_PRODUCT_PARAM)) ?? '',
      );
    }
  }

  /**
   * Removes the deep-link parameters from the address bar once they are
   * stored, so a reload does not open "Offerte anfragen" a second time and the
   * campaign values do not end up in bookmarks or shared links. Runs before
   * the router's first navigation, which then routes the cleaned URL.
   */
  stripFromAddressBar(): void {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      const keys = [...CAMPAIGN_KEYS, DEEP_LINK_PRODUCT_PARAM];
      if (!keys.some((key) => url.searchParams.has(key))) return;
      keys.forEach((key) => url.searchParams.delete(key));
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch {
      // Leave the URL as it is.
    }
  }

  /** All stored campaign values, or an empty object when there are none. */
  getCampaign(): CampaignValues {
    const raw = this.read(CAMPAIGN_STORAGE_KEY);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const campaign: CampaignValues = {};
      for (const key of CAMPAIGN_KEYS) {
        const value = parsed?.[key];
        if (typeof value === 'string' && value) campaign[key] = value;
      }
      return campaign;
    } catch {
      return {};
    }
  }

  /**
   * The `utm_parameter` for the `POST /api/Opportunities` body, or `null`
   * when it did not come from a link — the caller then omits the field and
   * the backend applies the product default.
   */
  getUtmParameter(): string | null {
    return this.getCampaign().utm_parameter ?? null;
  }

  /** Whether a deep link is waiting to open "Offerte anfragen". */
  hasPendingOfferProduct(): boolean {
    return this.read(PENDING_PRODUCT_STORAGE_KEY) !== null;
  }

  /**
   * Returns the product from the deep link and removes it, so the modal opens
   * only once. The campaign values stay for the rest of the session.
   *
   * - `null`: no deep link is waiting, do not open the modal;
   * - `''`: the link had an empty `product=`, open without a preselection;
   * - otherwise the product value to preselect.
   */
  consumePendingOfferProduct(): string | null {
    const product = this.read(PENDING_PRODUCT_STORAGE_KEY);
    this.remove(PENDING_PRODUCT_STORAGE_KEY);
    return product;
  }

  /** Removes all attribution. Logout already does this via `sessionStorage.clear()`. */
  clear(): void {
    this.remove(CAMPAIGN_STORAGE_KEY);
    this.remove(PENDING_PRODUCT_STORAGE_KEY);
  }

  // --- helpers ---------------------------------------------------------------

  private currentUrl(): string {
    return typeof window !== 'undefined' ? window.location.href : '';
  }

  private parseParams(url: string): URLSearchParams | null {
    if (!url) return null;
    try {
      return new URL(url, 'http://localhost').searchParams;
    } catch {
      return null;
    }
  }

  private sanitize(value: string | null): string | null {
    const trimmed = value?.trim();
    if (!trimmed) return null;
    return trimmed.slice(0, MAX_VALUE_LENGTH);
  }

  private read(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage unavailable (private mode, quota): attribution is best-effort.
    }
  }

  private remove(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // See write().
    }
  }
}
